import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ApiSuccess } from '../lib/api';

export function PersonaSetup() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [penName, setPenName] = useState('');
  const [genre, setGenre] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!penName.trim() || !genre.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await api.post<ApiSuccess<unknown>>('/personas/generate', {
        penName: penName.trim(),
        genre: genre.trim(),
      });
      // Invalidate so NewBook gets fresh persona data immediately without a refresh
      await qc.invalidateQueries({ queryKey: ['personas'] });
      void navigate('/books/new');
    } catch {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/"><img src="/logo.png" alt="StoryMint" className="h-12 w-auto mx-auto mb-8" /></a>
          <h1 className="text-2xl font-bold text-foreground mb-2">Create your pen name</h1>
          <p className="text-sm text-muted-foreground">
            This is the author identity your books will be written and published under. You can always add more later.
          </p>
        </div>

        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-5">
          <div>
            <label htmlFor="pen-name" className="block text-sm font-medium text-foreground mb-1">
              Pen Name
            </label>
            <input
              id="pen-name"
              type="text"
              value={penName}
              onChange={(e) => setPenName(e.target.value)}
              placeholder="e.g. J. Morgan Wells"
              className="w-full px-3 py-2.5 bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="genre" className="block text-sm font-medium text-foreground mb-1">
              Primary Genre
            </label>
            <select
              id="genre"
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              className="w-full px-3 py-2.5 bg-input border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">— Select a genre —</option>
              <optgroup label="Fiction">
                <option value="romance">Romance</option>
                <option value="thriller">Thriller</option>
                <option value="fantasy">Fantasy</option>
                <option value="science fiction">Science Fiction</option>
                <option value="mystery">Mystery</option>
                <option value="horror">Horror</option>
                <option value="historical fiction">Historical Fiction</option>
                <option value="literary fiction">Literary Fiction</option>
                <option value="young adult">Young Adult</option>
                <option value="adventure">Adventure</option>
              </optgroup>
              <optgroup label="Non-Fiction">
                <option value="self-help">Self-Help</option>
                <option value="business">Business</option>
                <option value="memoir">Memoir</option>
                <option value="true crime">True Crime</option>
              </optgroup>
            </select>
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <button
            type="submit"
            disabled={!penName.trim() || !genre || loading}
            className="w-full py-3 px-4 bg-primary text-primary-foreground font-semibold rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground" />
                Building your author profile…
              </span>
            ) : (
              'Create My Author Profile →'
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          We'll generate a professional author bio and writing style profile from your pen name and genre.
        </p>
      </div>
    </div>
  );
}
